import { Sigma } from "lucide-react";
import { cn } from "@/lib/utils";
import { budgetState, formatCost, formatTokens, usageLines } from "@/services/multi-agent/usageMeter";
import type { Usage } from "@/services/multi-agent/protocol";
import type { UsageTotals } from "@/services/multi-agent/laneV2/types";
import { selectUsage, useRunStateStore } from "@/stores/runStateStore";

/**
 * `RunState.usage` — tokens, cost, and the budget when there is one (§2.17; Tier 2 "usage meter").
 *
 * SEPARATE FROM THE COMPOSER'S USAGE RING, and the split is deliberate. This reports MONEY for the
 * run being watched, from `RunState.usage`; the ring reports TOKENS across the conversation, plus a
 * client-side estimate of what the next message will carry. Merging them would produce one figure
 * whose halves come from a measurement and a guess. Money keeps a permanent place because it has a
 * budget state — `warn` and `over` are things a reader acts on — where the token totals are a
 * question someone asks, which is why they sit behind the ring.
 *
 * `budget_pct` IS THE BACKEND'S. Nothing here divides cost by budget: frontend-rule §1 forbids
 * fabricating money figures, and a percentage derived locally would disagree with the number the
 * server used to decide whether to raise `approve_budget`. No budget means no bar — a 0% bar beside
 * "$0.00 of $—" claims a budget exists and is untouched.
 */
export interface UsageMeterViewProps {
  usage: Usage | null;
  /**
   * `compact` renders one header chip: total tokens, cost, and the budget percentage when there is
   * a budget. `full` renders the section with the per-field breakdown.
   *
   * TWO VARIANTS OF ONE COMPONENT, not two components: §8.3 permits two variants of a section and
   * forbids two parallel trees, and both variants must show the same FACTS — the chip omits detail,
   * never contradicts it. Level 1 is the chip in the run header; Level 2 is the section in the
   * workspace's Activity view, beside the tool calls it accounts for.
   */
  variant?: "full" | "compact";
  className?: string;
}

const BAR_STYLE = {
  ok: "bg-primary",
  warn: "bg-ma-warn",
  over: "bg-ma-err",
} as const;

export function UsageMeterView({ usage, variant = "full", className }: UsageMeterViewProps) {
  // No usage frame yet is the state every run starts in. An empty meter reading zero would claim
  // the run has done nothing, which is not the same as not having been told.
  if (!usage) return null;

  const budget = budgetState(usage);
  const lines = usageLines(usage);
  const total = usage.tokens_in + usage.tokens_out;

  if (variant === "compact") {
    /*
     * ── ONE NUMBER A PERSON CAN READ, NOT THREE A MACHINE EMITS ───────────────
     *
     * This chip read `Σ 10.3k $4.10 82%`: a maths glyph, a token count, a cost and a percentage,
     * none of them labelled. Three of the four are only legible to someone who already knows what
     * the chip is for — and the reader has to parse all four every time they glance at the header
     * to find the one that might matter.
     *
     * COST IS THE FACT THAT IS CONTINUOUSLY USEFUL. "What is this costing me" is a question people
     * genuinely hold while a run works. Token count is the unit the cost is BUILT from — the same
     * fact one level less useful — and the budget percentage is only actionable as it approaches
     * the ceiling. So the chip shows the cost, and:
     *
     *   - the percentage appears only in `warn`/`over`, which are the states someone acts on (and
     *     which the backend also raises `approve_budget` for, §5.4). Below that it is silence.
     *   - tokens in / out / cached keep their home in the `full` variant under Activity, beside the
     *     tool calls that account for them. Nothing is deleted — §16 of the rules: clean is fewer
     *     competing elements, not fewer capabilities.
     *
     * The `title` still names the whole thing, so the chip explains itself on hover without
     * growing a popover.
     */
    const showPct = budget.state === "warn" || budget.state === "over";
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-[11px] tabular-nums text-muted-foreground",
          // The one place colour is spent: over budget is the only state here anyone acts on.
          // No border in the resting state — a bordered pill reads as a control, and this is a readout.
          budget.state === "over" && "border border-ma-err-line text-[var(--ma-state-err-text)]",
          budget.state === "warn" && "border border-ma-warn-line text-[var(--ma-state-warn-text)]",
          className,
        )}
        // The breakdown lives in the workspace; this says where, rather than growing a popover.
        title={`This run has used ${formatTokens(total)} tokens · ${formatCost(usage.cost_usd)}${
          budget.state !== "none" ? ` · ${budget.pct.toFixed(0)}% of budget` : ""
        } — full breakdown under Activity`}
      >
        <span>{formatCost(usage.cost_usd)}</span>
        {showPct && <span>{budget.pct.toFixed(0)}% of budget</span>}
      </span>
    );
  }

  return (
    <section
      aria-labelledby="weave-usage-title"
      className={cn("rounded-md border border-border bg-card px-3 py-2", className)}
    >
      <header className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <Sigma aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <h3 id="weave-usage-title" className="text-[13px] font-semibold text-foreground">
          Usage
        </h3>
        <span className="tabular-nums text-[12px] text-muted-foreground">
          {formatTokens(total)} tokens
        </span>
        <span className="tabular-nums text-[12px] text-foreground">{formatCost(usage.cost_usd)}</span>
      </header>

      {budget.state !== "none" && (
        <div className="mt-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12px] text-muted-foreground">
              {/* The server's own percentage, and the budget it is a percentage OF. */}
              {budget.pct.toFixed(0)}% of {formatCost(budget.budgetUsd)}
            </span>
            {budget.state === "over" && (
              // `role="status"` not `alert`: the gate is the backend's `approve_budget` interaction,
              // which raises its own card. This is the figure, not the interruption.
              <span role="status" className="text-[12px] font-medium text-ma-err-text">
                Over budget
              </span>
            )}
          </div>
          <div
            role="progressbar"
            aria-labelledby="weave-usage-title"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(Math.min(100, budget.pct))}
            className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          >
            <div
              className={cn("h-full rounded-full", BAR_STYLE[budget.state])}
              // Capped at 100% so an over-budget run does not paint past its track; the "Over
              // budget" word above carries the fact that it exceeded.
              style={{ width: `${Math.min(100, Math.max(0, budget.pct))}%` }}
            />
          </div>
        </div>
      )}

      {/*
        Intrinsic reflow rather than breakpoints: this sits in the run header on desktop and in a
        narrow panel when the sidebar is open, and the space it has is not the width of the window.
      */}
      <dl className="mt-1.5 grid gap-x-3 gap-y-0.5 [grid-template-columns:repeat(auto-fit,minmax(min(104px,100%),1fr))]">
        {lines.map((line) => (
          <div key={line.label} className="flex items-baseline justify-between gap-1.5">
            <dt className="truncate text-[11px] text-muted-foreground">{line.label}</dt>
            {/*
              An em dash, never `0`. A reported 0 is a measurement; an absent field means the
              backend did not report that thing at all, and rendering them alike turns "we don't
              know" into "we measured nothing".
            */}
            <dd
              className="shrink-0 tabular-nums text-[11px] text-foreground"
              title={line.value === null ? "Not reported for this run" : undefined}
            >
              {line.value ?? "—"}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Wired. Split from the view for the reason `ActionBar`/`ActionBarView` are split. */
export function UsageMeter({
  variant,
  className,
}: {
  variant?: UsageMeterViewProps["variant"];
  className?: string;
}) {
  const usage = useRunStateStore(selectUsage);
  return <UsageMeterView usage={usage} variant={variant} className={className} />;
}

/**
 * Lane v2's own usage meter — R1 `usage_total` (the conversation's running spend) or R5's per-turn
 * `usage`, both `UsageTotals` (`laneV2/types.ts`): `{cost_usd, tokens_in, tokens_out, tool_calls}`.
 *
 * A SEPARATE COMPONENT, NOT `UsageMeterView` REUSED. `Usage` (the legacy/v1 shape) requires
 * `duration_ms` and carries budget/cache/hop fields lane v2 has no wire equivalent of at all — not
 * "not reported", genuinely absent from this surface's shape. Coercing a `UsageTotals` into `Usage`
 * would mean inventing a duration (frontend rule: never fabricate a usage figure) or reaching past
 * the type system with a cast; a few rows of intentionally narrower markup, sharing the same
 * `formatCost`/`formatTokens` helpers `usageMeter.ts` already exports, is the honest alternative.
 */
export interface LaneV2UsageMeterProps {
  usageTotal: UsageTotals | null;
  variant?: "full" | "compact";
  className?: string;
}

export function LaneV2UsageMeterView({ usageTotal, variant = "full", className }: LaneV2UsageMeterProps) {
  if (!usageTotal) return null;
  const total = usageTotal.tokens_in + usageTotal.tokens_out;

  if (variant === "compact") {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-[11px] tabular-nums text-muted-foreground",
          className,
        )}
        title={`This conversation has used ${formatTokens(total)} tokens · ${formatCost(usageTotal.cost_usd)}`}
      >
        {formatCost(usageTotal.cost_usd)}
      </span>
    );
  }

  return (
    <section
      aria-labelledby="lanev2-usage-title"
      className={cn("rounded-md border border-border bg-card px-3 py-2", className)}
    >
      <header className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <Sigma aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <h3 id="lanev2-usage-title" className="text-[13px] font-semibold text-foreground">
          Usage
        </h3>
        <span className="tabular-nums text-[12px] text-muted-foreground">{formatTokens(total)} tokens</span>
        <span className="tabular-nums text-[12px] text-foreground">{formatCost(usageTotal.cost_usd)}</span>
      </header>
      <dl className="mt-1.5 grid gap-x-3 gap-y-0.5 [grid-template-columns:repeat(auto-fit,minmax(min(104px,100%),1fr))]">
        {[
          { label: "Tokens in", value: formatTokens(usageTotal.tokens_in) },
          { label: "Tokens out", value: formatTokens(usageTotal.tokens_out) },
          { label: "Tool calls", value: String(usageTotal.tool_calls) },
        ].map((line) => (
          <div key={line.label} className="flex items-baseline justify-between gap-1.5">
            <dt className="truncate text-[11px] text-muted-foreground">{line.label}</dt>
            <dd className="shrink-0 tabular-nums text-[11px] text-foreground">{line.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** A per-turn chip — the same compact rendering, inline rather than in a card. */
export function LaneV2TurnUsageChip({ usage, className }: { usage: UsageTotals | null; className?: string }) {
  if (!usage) return null;
  return (
    <span
      className={cn("inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground", className)}
      title={`This turn used ${formatTokens(usage.tokens_in + usage.tokens_out)} tokens · ${formatCost(usage.cost_usd)}`}
    >
      {formatCost(usage.cost_usd)}
    </span>
  );
}
